import { PageHeaderFilterBarSlotProvider } from "@repo/ui/Navigation/pageHeaderFilterBarSlot";
import { PageWidth, pageWidthClassNames } from "@repo/ui/Navigation/pageWidth";
import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useState,
  type FC,
  type PropsWithChildren,
  type ReactNode,
} from "react";
import { twMerge } from "tailwind-merge";
import { tv } from "tailwind-variants";

export interface PageLayoutProps extends PropsWithChildren {
  /**
   * Stretch the shell to at least the viewport so a `PageBody fills` child can
   * grow and anchor inner sticky elements (e.g. a sticky sidebar).
   */
  fillsHeight?: boolean;
  className?: string;
}

/**
 * The outer shell of a standard page: a flex column that scrolls in the app's
 * main scroll container beneath the sticky `PageHeader`/`FilterBar`. Full-height
 * editor experiences (playbook, voice agent, and signal editors) are the only
 * pages that use their own shell instead.
 */
export const PageLayout: FC<PageLayoutProps> = ({
  fillsHeight = false,
  className,
  children,
}) => (
  <div
    className={twMerge("flex flex-col", fillsHeight && "min-h-full", className)}
  >
    {children}
  </div>
);

const pageBodyStyle = tv({
  base: "flex flex-col gap-4 px-4 py-4 sm:gap-6 sm:px-6 sm:py-6",
  variants: {
    fills: {
      true: "min-h-0 flex-1",
    },
  },
});

export interface PageBodyProps extends PropsWithChildren {
  /** Grow to fill a `PageLayout fillsHeight` shell. */
  fills?: boolean;
  /**
   * The content column the body is centered in. Pass the same value to the
   * page's `PageHeader` so the two stay aligned.
   * @default PageWidth.Full
   */
  width?: PageWidth;
  className?: string;
}

/**
 * The standard content wrapper directly below the page header area: the page
 * gutter padding and vertical rhythm every page shares.
 */
export const PageBody: FC<PageBodyProps> = ({
  fills = false,
  width = PageWidth.Full,
  className,
  children,
}) => (
  <div
    className={pageBodyStyle({
      fills,
      className: twMerge(
        pageWidthClassNames[width],
        // Keeps settings forms from collapsing below a usable width. The
        // header shares the column but not the floor, so its title row can
        // still shrink.
        width === PageWidth.Form && "min-w-[450px]",
        className,
      ),
    })}
  >
    {children}
  </div>
);

const FullHeightPageContentContext = createContext<(() => () => void) | null>(
  null,
);

export interface TabbedPageLayoutProps extends PropsWithChildren {
  /** The page's `PageHeader` (typically with `tabs` and a `PageHeaderFilterBarSlot`). */
  header: ReactNode;
}

/**
 * Shell for a tabbed page whose child routes are mostly flowing pages but may
 * include bounded full-height experiences. Child routes declare themselves
 * via `FullHeightPageContent`; the shell then switches from a flow column to
 * a full-height grid with a scroll region — same tree, only the classes
 * change, so the header and any providers keep their state across the
 * switch. Includes the `PageHeaderFilterBarSlotProvider` so tabs can render
 * their filter bars into the header's slot.
 */
export const TabbedPageLayout: FC<TabbedPageLayoutProps> = ({
  header,
  children,
}) => {
  const [fullHeightCount, setFullHeightCount] = useState(0);
  const register = useCallback(() => {
    setFullHeightCount((count) => count + 1);
    return () => setFullHeightCount((count) => count - 1);
  }, []);

  const isFullHeight = fullHeightCount > 0;

  return (
    <PageHeaderFilterBarSlotProvider>
      <FullHeightPageContentContext.Provider value={register}>
        <div
          className={
            isFullHeight
              ? "grid h-full grid-rows-[auto_minmax(0,1fr)] overflow-hidden"
              : "flex flex-col"
          }
        >
          {header}
          <div className={isFullHeight ? "relative overflow-auto" : "contents"}>
            {children}
          </div>
        </div>
      </FullHeightPageContentContext.Provider>
    </PageHeaderFilterBarSlotProvider>
  );
};

/**
 * Declares the enclosing child route as a bounded full-height experience: the
 * ancestor `TabbedPageLayout` gives it a definite-height scroll region to
 * fill instead of the flowing page column. Registration happens in a layout
 * effect, so the shell switches before paint.
 */
export const FullHeightPageContent: FC<PropsWithChildren> = ({ children }) => {
  const register = useContext(FullHeightPageContentContext);
  useLayoutEffect(() => {
    if (!register) {
      throw new Error(
        "FullHeightPageContent requires a TabbedPageLayout ancestor.",
      );
    }
    return register();
  }, [register]);
  return <>{children}</>;
};

export interface TableCardProps extends PropsWithChildren {
  className?: string;
}

/**
 * Bordered card that frames a table inside a `PageBody`. The page scrolls past
 * it, so in-table sticky headers are forced off; render the table itself full
 * bleed — the card provides the single border and rounding.
 */
export const TableCard: FC<TableCardProps> = ({ className, children }) => (
  <div
    className={twMerge(
      "max-w-full overflow-x-auto overflow-y-hidden rounded-lg border border-border-light bg-white [&_thead]:!static [&_thead]:!z-auto",
      className,
    )}
  >
    {children}
  </div>
);
